<template>
  <div v-if="shouldShowProgressBar" class="parent-progress-bar-root" :class="{ 'negative-margin': !isFlatProgressBar }">
    <!-- Flat bar: single progress bar for all steps -->
    <template v-if="isFlatProgressBar">
      <div class="flat-bar-wrapper">
        <div class="flat-bar-bg"></div>
        <div class="flat-bar-fill" :style="{ width: flatFillPct + '%' }"></div>
      </div>
    </template>

    <!-- Mobile layout: numbered parent nodes with segments -->
    <template v-else-if="isMobileProgressBar">
      <div v-if="findCurrentParentIdx() >= 0" class="active-parent-label">
        {{ findCurrentParentIdx() + 1 }}.
        {{ parentLabels[parents[findCurrentParentIdx()].label] || parents[findCurrentParentIdx()].label }}
      </div>
      <div class="parent-progress-bar mobile-layout">
        <template v-for="(parent, idx) in parents">
          <div :key="parent.id || idx" class="bar-parent-node">
            <div class="parent-circle" :class="circleClasses(idx)">
              <template v-if="idx === parents.length - 1">
                <img :src="require('@/assets/checkmark-gray.svg')" alt="" width="12px" height="12px" />
              </template>
              <template v-else>
                {{ idx + 1 }}
              </template>
            </div>
          </div>
          <!-- Segment/bar if not last -->
          <div v-if="idx < parents.length - 1" :key="'segment-' + idx" class="bar-segment-wrapper">
            <div class="bar-segment-bg"></div>
            <div class="bar-segment-fill" :style="{ width: fillPct(idx) + '%' }"></div>
          </div>
        </template>
      </div>
    </template>

    <!-- Desktop layout: parent nodes with segments -->
    <template v-else>
      <div class="parent-progress-bar desktop-layout">
        <template v-for="(parent, idx) in parents">
          <div :key="parent.id || idx" class="bar-parent-node">
            <div class="parent-circle" :class="circleClasses(idx)"></div>
            <div
              :class="['parent-label', idx === 0 ? 'first-label' : '', idx === parents.length - 1 ? 'last-label' : '']"
            >
              {{ parentLabels[parent.label] || parent.label }}
            </div>
          </div>
          <!-- Segment/bar if not last -->
          <div v-if="idx < parents.length - 1" :key="'segment-' + idx" class="bar-segment-wrapper">
            <div class="bar-segment-bg"></div>
            <div class="bar-segment-fill" :style="{ width: fillPct(idx) + '%' }"></div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { buildParentProgressBarStructure } from '@/views/shared/progress-bar/progressRoutesUtil';

export default {
  name: 'ProgressBar',
  props: {
    // Array of routes for the flow ( REQUIRED )
    routes: {
      type: Array,
      required: true,
    },

    /**  All other props are optional ** */
    // Function to filter routes ( Custom logic to hide steps )
    filterFn: {
      type: Function,
      default: () => () => true,
    },
    // Function to generate custom labels ( Custom Mapping for parent sections )
    labelsFn: {
      type: Function,
      default: () => () => true,
    },
    // Hide progress bar on the final step ( We hide by default on the last step )
    shouldHideOnFinalStep: {
      type: Boolean,
      default: true,
    },
    // Custom labels for parent sections ( e.g. { "personalInformation": "label", "idVerification": "label" } )
    labels: {
      type: Object,
      default: () => null,
    },
    // Render progress bar in a flat layout ( no steps, just a single bar )
    isFlatLayout: {
      type: Boolean,
      default: false,
    },
    // Start flat bar with first step filled ( Useful Only for flat progress bars to fill the first step )
    startWithFirstStepFilled: {
      type: Boolean,
      default: true,
    },
    // Layout type (desktop or mobile) ( Overrides the barLayout computed property for external use )
    isMobileLayout: {
      type: Boolean,
      default: false,
    },
    // Current route name ( Used to determine the active step )
    currentRouteName: {
      type: String,
      default: null,
    },
  },
  computed: {
    // Determine the layout is mobile or desktop based on the prop or route
    isMobileProgressBar() {
      if (this.isMobileLayout) {
        return true;
      }
      return this.baseRoute === 'mobile' || this.isMobileScreenSize;
    },

    // Determine if the progress bar should be flat or not. From the current route's meta "progressBarType" or defaulting to the prop
    isFlatProgressBar() {
      if (this.isFlatLayout) return true;

      const currentRoute = this.routes.find(
        r => r.children && r.children.some(child => child.name === this.$route.name)
      );
      const progressBarType =
        currentRoute && currentRoute.meta && currentRoute.meta.progressBarType
          ? currentRoute.meta.progressBarType
          : 'flat';

      return progressBarType === 'flat';
    },

    // Generate Labels for each parent step, either from labels prop or computed from labelsFn
    // Ex. { "personalInformation": "label", "idVerification": "label","results": "label" }
    parentLabels() {
      const parentSectionKeys = this.parents.reduce((acc, parent) => {
        acc[parent.label] = 'label';
        return acc;
      }, {});

      return this.labels ? this.labels : this.labelsFn(parentSectionKeys);
    },

    // Obtain all the "children: []" from the route and Filter routes based on current route and filterFn
    filteredRoutes() {
      let filtered = [];
      const currentRoute = this.routes.find(
        r => r.children && r.children.some(child => child.name === this.$route.name)
      );

      if (currentRoute && currentRoute.meta && currentRoute.meta.progressBarType) {
        filtered = currentRoute.children || [];
      }

      return filtered.filter(this.filterFn);
    },

    // Defaults to Vue Router route name if not provided
    activeRouteName() {
      return this.currentRouteName || (this.$route && this.$route.name);
    },

    // Build out parent (section) steps, computing their structure and substep completeness
    parents() {
      return buildParentProgressBarStructure(this.filteredRoutes, {
        currentRouteName: this.activeRouteName,
        completeFn: this.routeCompleteFn,
      });
    },

    // The progress bar will be hidden on the final step or the current route does not match the filtered routes
    shouldShowProgressBar() {
      if (this.shouldHideOnFinalStep && this.isFinalStep) {
        return false;
      }
      const creditFormChildrenPaths = this.filteredRoutes.map(child => child.path);
      // Handling special cases like case `carfax-details-form` = `:year/:make/:model/carfax-details-form`
      const creditFormChildrenBasePaths = this.filteredRoutes.map(child => child.meta?.basePath);
      const currentPathSegment = this.$route.path.split('/').pop();
      return (
        this.$route.meta &&
        (creditFormChildrenPaths.includes(currentPathSegment) ||
          creditFormChildrenBasePaths.includes(currentPathSegment))
      );
    },

    // Check if the current step is the final step in the flow
    isFinalStep() {
      // last step = last substep in last parent
      const { parents } = this;
      if (!parents || !parents.length) return false;
      const lastParent = parents[parents.length - 1];
      if (!lastParent || !lastParent.substeps || !lastParent.substeps.length) return false;
      const lastSubstep = lastParent.substeps[lastParent.substeps.length - 1];
      return this.activeRouteName === lastSubstep.name;
    },

    // Finds current parent section (group) for this route
    currentParentSection() {
      const route = this.filteredRoutes.find(r => r.name === this.activeRouteName);
      return (route && route.meta && route.meta.parentSection) || null;
    },
    // What is the step order inside the parent section
    currOrderInParent() {
      const route = this.filteredRoutes.find(r => r.name === this.activeRouteName);
      if (!route || !route.meta || !this.currentParentSection) {
        return -1;
      }

      return typeof route.meta.order === 'number' ? route.meta.order : this.filteredRoutes.indexOf(route);
    },
    // Helper for global ordering/completion by parentSection id
    parentSectionsInOrder() {
      return this.filteredRoutes.map(route => route.meta && route.meta.parentSection).filter(Boolean);
    },

    // Flat bar: total number of steps across all parents (All visible substeps, sequentially)
    flatNumSteps() {
      return this.parents.reduce((sum, parent) => sum + parent.substeps.length, 0);
    },

    // The index of the current step among all visible steps (flattened order)
    flatCurrentStepIndex() {
      let idx = 0;
      // eslint-disable-next-line no-restricted-syntax
      for (const parent of this.parents) {
        // eslint-disable-next-line no-restricted-syntax
        for (const ss of parent.substeps) {
          if (ss.name === this.activeRouteName) {
            return idx;
          }
          idx += 1;
        }
      }
      return 0;
    },

    // Flat bar: fill is up to and including current step
    flatFillPct() {
      if (this.flatNumSteps <= 1) return 0;
      const flatCurrentStepIndex = this.startWithFirstStepFilled
        ? this.flatCurrentStepIndex + 1
        : this.flatCurrentStepIndex;
      const flatNumSteps = this.startWithFirstStepFilled ? this.flatNumSteps : this.flatNumSteps - 1;
      return Math.round((flatCurrentStepIndex / flatNumSteps) * 100);
    },
  },
  methods: {
    // Determines whether a step is completed. Passed to progress builder utility.
    routeCompleteFn(route) {
      const currParentSection = this.currentParentSection;
      const { currOrderInParent } = this;

      // Active parent: only up to and including the current step/order
      if (currParentSection && route.meta && route.meta.parentSection === currParentSection) {
        const order = typeof route.meta.order === 'number' ? route.meta.order : 0;
        return order < currOrderInParent;
      }

      // Any prior parent: everything completed in those
      return !!(
        route.meta &&
        route.meta.parentSection &&
        this.parentSectionOrder(route.meta.parentSection) < this.parentSectionOrder(currParentSection)
      );
    },

    // Find zero-based index of a parentSection id for ordering
    parentSectionOrder(section) {
      return this.parentSectionsInOrder.indexOf(section);
    },

    // Used by visual rendering:
    isParentCompleted(idx) {
      if (!this.parents[idx]) return false;
      const done = (this.parents[idx].substeps || []).every(s => s.completed);
      const current = this.findCurrentParentIdx();
      return idx < current || (done && idx === current);
    },
    isParentPartiallyCompleted(idx) {
      if (!this.parents[idx]) return false;
      return (this.parents[idx].substeps || []).some(s => s.completed);
    },
    isParentActive(idx) {
      return idx === this.findCurrentParentIdx();
    },

    circleClasses(idx) {
      const isCompleted = this.isParentCompleted(idx) || this.isParentPartiallyCompleted(idx);
      const isActive = this.isParentActive(idx);

      return {
        completed: isCompleted,
        active: isActive && !isCompleted, // Only mark as active if not completed
        disabled: this.parents[idx]?.disabled,
      };
    },

    // Calculate the fill percentage for each parent step
    fillPct(idx) {
      const parent = this.parents[idx];
      if (!parent || !parent.substeps) return 0;
      const steps = parent.substeps;
      if (!steps.length) return 0;

      // Find the index of the current substep in this parent
      const currParentIdx = this.findCurrentParentIdx();
      const currSubstepIdx = steps.findIndex(s => s.name === this.activeRouteName);

      if (currParentIdx > idx) {
        return 100;
      }
      if (currParentIdx === idx) {
        const filled = Math.max(0, currSubstepIdx);
        return Math.round((filled / steps.length) * 100);
      }
      return 0;
    },

    // Find the index of the current parent section in the parents array
    findCurrentParentIdx() {
      // Current section id:
      const currId = this.currentParentSection;
      if (!currId) return 0;
      for (let i = 0; i < this.parents.length; i += 1) {
        if (this.parents[i] && String(this.parents[i].id) === String(currId)) return i;
      }
      return 0;
    },
  },
};
</script>

<style scoped>
.parent-progress-bar-root {
  width: 100%;
}

.parent-progress-bar {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
}

.bar-parent-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 48px;
  width: 48px;
  position: relative;
  z-index: 2;
}

/* CIRCLE */
.parent-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #dae2ea;
  margin-bottom: 8px;
  margin-top: 2px;
  position: relative;
  z-index: 3;
  box-sizing: border-box;
}

.parent-circle.completed {
  background: #007bc7;
  border-color: #007bc7;
}

.parent-circle.active {
  background: white;
  border: 2px solid #007bc7;
  box-shadow: none;
}

.parent-label {
  font-size: 12px;
  font-weight: 400;
  color: #4b5863;
  text-align: center;
  width: 113px;
}

.first-label {
  text-align: left;
  margin-left: 6.2rem;
}

.last-label {
  text-align: right;
  margin-right: 6rem;
}

/* CONNECTING BAR */
.bar-segment-wrapper {
  position: relative;
  flex: 1;
  min-width: 40px;
  height: 6px;
  margin-top: 7px;
  margin-left: -20px;
  margin-right: -20px;
  z-index: 1;
}

.bar-segment-bg {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: #dae2ea;
  border-radius: 2px;
  width: 100%;
  height: 6px;
  z-index: 1;
}

.bar-segment-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #007bc7;
  border-radius: 2px;
  height: 6px;
  transition: width 0.22s;
  z-index: 2;
}

/* FLAT BAR */
.flat-bar-wrapper {
  margin-top: 18px;
  position: relative;
  width: 100%;
  height: 6px;
}
.flat-bar-bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 6px;
  background: #dae2ea;
  border-radius: 2px;
  z-index: 1;
}
.flat-bar-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 6px;
  background: #007bc7;
  border-radius: 2px;
  z-index: 2;
  transition: width 0.22s;
}

/* MOBILE LAYOUT */
.parent-progress-bar.mobile-layout {
  .parent-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    text-align: center;
    background: #ffffff;
    color: #6d7783;
    border: 1px solid #dae2ea;
    position: relative;
    font-weight: 400;
    line-height: 20px;
    z-index: 3;
    box-sizing: border-box;
    padding: 2px;
    margin-top: 0;
    font-size: 12px;
    img {
      margin-bottom: 3px;
    }
  }
  .bar-segment-wrapper,
  .bar-segment-bg,
  .bar-segment-fill {
    height: 4px;
  }
}

.active-parent-label {
  font-weight: 700;
  font-size: 14px;
  line-height: 17.5px;
  margin-bottom: 10px;
  margin-left: 12px;
}

.parent-progress-bar.mobile-layout .bar-segment-bg,
.parent-progress-bar.mobile-layout .bar-segment-fill {
  top: 3px;
}

.parent-progress-bar.mobile-layout .parent-circle.active {
  background: #ffffff;
  color: #007bc7;
  border-color: #007bc7;
}

.parent-progress-bar.mobile-layout .parent-circle.completed {
  background: #007bc7;
  border-color: #007bc7;
  color: #ffffff;
}
</style>
